import type { HTMLAttributes } from "react";

export type NavStatusKind = "coming-soon" | "nice-to-have";

export interface NavStatusBadgeProps extends HTMLAttributes<HTMLSpanElement> {
  kind: NavStatusKind;
}

const LABELS: Record<NavStatusKind, string> = {
  "coming-soon": "coming soon",
  "nice-to-have": "nice to have",
};

/**
 * NavStatusBadge — the pastel status pill used by the v37 stage-based
 * navigation (sidebar section/item) and the welcome home-grid to mark a
 * surface that is not yet built.
 *
 * v37 design vocabulary (`.badge-coming-soon` / `.badge-nice-to-have`):
 *   - coming-soon: amber pastel — bg/border/text on the design-system
 *     `--warning` (#ffb800) token family (`warning-muted` bg,
 *     `warning-strong` border, `warning` text).
 *   - nice-to-have: sky pastel — Tailwind `sky-300` at 15%/25% opacity
 *     (no sky token in the design system; mirrors how `StatusBadge`
 *     uses `blue-400` for the `bd` variant).
 *
 * Shape matches the other pills (`NewBadge`/`ContextBadge`): pill radius,
 * 10px/600 `text-micro`, slight letter-spacing, `text-transform:none`
 * (Tailwind utilities never uppercase, so no override needed).
 *
 * Tokenized: token color utilities + `text-micro` role + `rounded-full`
 * (`--radius-full`). No inline styles, no arbitrary text sizes.
 */
export function NavStatusBadge({ kind, className, ...rest }: NavStatusBadgeProps) {
  const variants: Record<NavStatusKind, string> = {
    "coming-soon": "bg-warning-muted border border-warning-strong text-warning",
    "nice-to-have": "bg-sky-300/15 border border-sky-300/25 text-sky-300",
  };
  const base =
    "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-micro tracking-[0.02em] whitespace-nowrap";
  const classes = [base, variants[kind], className].filter(Boolean).join(" ");
  return (
    <span {...rest} className={classes}>
      {LABELS[kind]}
    </span>
  );
}
